import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { getQuizBySlug } from "../../services/firestoreService";
import Navbar from "../Dashboard/NavBar";
import DashboardHeader from "../Dashboard/Header";
import Avatar from "../../Assets/avatar.png";
import { Clock, Navigation, Eye, Shuffle, Play, Home, ChevronRight, RefreshCw } from "lucide-react";
import warningIcon from "../../Assets/icons/warning-filled-svgrepo-com.svg";

function QuizOverview() {
  const { quizSlug } = useParams();
  const navigate = useNavigate();
  const [showDropdown, setShowDropdown] = useState(false);
  const [quiz, setQuiz] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showStartConfirmation, setShowStartConfirmation] = useState(false);

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  useEffect(() => {
    const fetchQuizData = async () => {
      try {
        setLoading(true);

        // Fetch quiz by slug
        const quizResult = await getQuizBySlug(quizSlug);

        if (!quizResult.success) {
          console.error("Quiz not found");
          navigate('/main');
          return;
        }

        setQuiz(quizResult.data);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching quiz:", err);
        navigate('/main');
      }
    };

    fetchQuizData();
  }, [quizSlug, navigate]);

  const handleStartQuiz = () => {
    setShowStartConfirmation(true);
  };

  const confirmStartQuiz = () => {
    setShowStartConfirmation(false);
    // Generate a secure session token for this quiz attempt
    const sessionToken = `qst_${Date.now()}_${Math.random().toString(36).substr(2, 12)}`;
    sessionStorage.setItem(`quiz_session_${quiz.slug}`, sessionToken);
    navigate(`/quiz/${quiz.slug}/take`, { state: { sessionToken } });
  };

  if (loading) {
    return (
      <div className="flex h-screen overflow-hidden bg-gray-100">
        <Navbar />
        <main className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
            <p className="text-gray-600 font-medium">Loading quiz...</p>
          </div>
        </main>
      </div>
    );
  }

  if (!quiz) {
    return null;
  }

  const timeLimit = quiz.settings?.timeLimit || null;
  const navigationType = quiz.settings?.navigationType || 'free';
  const reviewPermission = quiz.settings?.reviewPermission || 'full';
  const shuffleQuestions = quiz.settings?.shuffleQuestions || false;
  const randomizeOptions = quiz.settings?.randomizeOptions || false;
  const attemptLimit = quiz.settings?.attemptLimit || 'unlimited';
  const maxAttempts = quiz.settings?.maxAttempts || null;

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Side Navigation Bar */}
      <Navbar />

      {/* Main Content Area */}
      <main className="flex-1 flex flex-col overflow-hidden">
        {/* Dashboard Header */}
        <div className="flex-shrink-0 py-4 md:py-6 px-4 md:px-8 bg-gray-50">
          <DashboardHeader
            toggleDropdown={toggleDropdown}
            showDropdown={showDropdown}
            Avatar={Avatar}
          />
        </div>

        {/* Content - Scrollable with custom scrollbar */}
        <style>{`
          .quiz-overview-content::-webkit-scrollbar {
            width: 8px;
          }
          .quiz-overview-content::-webkit-scrollbar-track {
            background: #f8fafc;
            border-radius: 10px;
          }
          .quiz-overview-content::-webkit-scrollbar-thumb {
            background: #cbd5e1;
            border-radius: 10px;
          }
          .quiz-overview-content::-webkit-scrollbar-thumb:hover {
            background: #94a3b8;
          }
        `}</style>
        <div className="flex-1 px-4 md:px-8 pb-8 overflow-y-auto quiz-overview-content">
          <div className="max-w-4xl mx-auto">

            {/* Breadcrumb Navigation */}
            {quiz.category && (
              <nav className="flex items-center gap-2 text-sm mb-6" aria-label="Breadcrumb">
                <Link
                  to="/main"
                  className="flex items-center gap-1.5 text-gray-500 hover:text-emerald-600 transition-colors"
                >
                  <Home className="w-4 h-4" />
                  <span className="font-medium">Home</span>
                </Link>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <Link
                  to={`/category/${quiz.categorySlug || quiz.categoryId}`}
                  className="text-gray-500 hover:text-emerald-600 font-medium transition-colors"
                >
                  {quiz.categoryName || 'Category'}
                </Link>
                <ChevronRight className="w-4 h-4 text-gray-400" />
                <span className="text-gray-900 font-semibold" aria-current="page">
                  {quiz.title}
                </span>
              </nav>
            )}

            {/* Compact Header with Start Button */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden mb-6">
              <div className="grid md:grid-cols-[1fr,auto] gap-6 p-6 items-center">
                {/* Left: Title & Description */}
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                    {quiz.title}
                  </h1>
                  {quiz.description && (
                    <p className="text-gray-600 text-base leading-relaxed">
                      {quiz.description}
                    </p>
                  )}
                </div>

                {/* Right: Start Button */}
                <div className="flex items-center justify-center md:justify-end">
                  <button
                    onClick={handleStartQuiz}
                    className="inline-flex items-center gap-2 bg-emerald-600 text-white font-semibold py-3 px-8 rounded-lg transition-all duration-200 hover:bg-emerald-700 shadow-md hover:shadow-lg transform hover:scale-105 whitespace-nowrap"
                  >
                    <Play className="w-5 h-5" />
                    <span>Start Quiz</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Quiz Settings - Always show all settings */}
            <div className="bg-white rounded-xl p-6 shadow-sm mb-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Quiz Settings</h2>
              <div className="grid md:grid-cols-2 gap-4">

                {/* Navigation Type - Always show */}
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <Navigation className={`w-5 h-5 mt-0.5 flex-shrink-0 ${navigationType === 'free' ? 'text-emerald-600' : 'text-orange-600'}`} />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm mb-1">
                      {navigationType === 'free' ? 'Free Navigation' : 'Sequential Navigation'}
                    </p>
                    <p className="text-xs text-gray-600">
                      {navigationType === 'free'
                        ? 'Jump to any question anytime'
                        : 'Answer questions in order'}
                    </p>
                  </div>
                </div>

                {/* Review Permission - Always show */}
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <Eye className={`w-5 h-5 mt-0.5 flex-shrink-0 ${reviewPermission === 'full' ? 'text-blue-600' : reviewPermission === 'partial' ? 'text-purple-600' : 'text-gray-600'}`} />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm mb-1">
                      {reviewPermission === 'full' ? 'Full Review' : reviewPermission === 'partial' ? 'Score Only' : 'No Review'}
                    </p>
                    <p className="text-xs text-gray-600">
                      {reviewPermission === 'full'
                        ? 'See answers and explanations'
                        : reviewPermission === 'partial'
                        ? 'Only final score shown'
                        : 'No feedback provided'}
                    </p>
                  </div>
                </div>

                {/* Time Limit - Always show */}
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <Clock className={`w-5 h-5 mt-0.5 flex-shrink-0 ${timeLimit ? 'text-blue-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm mb-1">
                      {timeLimit ? `${timeLimit} Minutes` : 'No Time Limit'}
                    </p>
                    <p className="text-xs text-gray-600">
                      {timeLimit ? 'Timer starts when you begin' : 'Take as long as you need'}
                    </p>
                  </div>
                </div>

                {/* Attempt Limit - Always show */}
                <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
                  <RefreshCw className={`w-5 h-5 mt-0.5 flex-shrink-0 ${attemptLimit === 'unlimited' ? 'text-emerald-600' : 'text-purple-600'}`} />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm mb-1">
                      {attemptLimit === 'unlimited' ? 'Unlimited Attempts' : attemptLimit === 'once' ? 'One Attempt Only' : `${maxAttempts} Attempts`}
                    </p>
                    <p className="text-xs text-gray-600">
                      {attemptLimit === 'unlimited'
                        ? 'Retake as many times as you want'
                        : attemptLimit === 'once'
                        ? 'You can only take this quiz once'
                        : `You can retake up to ${maxAttempts} times`}
                    </p>
                  </div>
                </div>

                {/* Shuffle Questions - Always show (even if disabled) */}
                <div className={`flex items-start gap-3 p-4 rounded-lg ${shuffleQuestions ? 'bg-indigo-50 border-2 border-indigo-200' : 'bg-gray-50'}`}>
                  <Shuffle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${shuffleQuestions ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm mb-1">
                      {shuffleQuestions ? 'Questions Shuffled' : 'Questions Not Shuffled'}
                    </p>
                    <p className="text-xs text-gray-600">
                      {shuffleQuestions ? 'Random question order each time' : 'Questions appear in fixed order'}
                    </p>
                  </div>
                </div>

                {/* Randomize Options - Always show (even if disabled) */}
                <div className={`flex items-start gap-3 p-4 rounded-lg ${randomizeOptions ? 'bg-indigo-50 border-2 border-indigo-200' : 'bg-gray-50'}`}>
                  <Shuffle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${randomizeOptions ? 'text-indigo-600' : 'text-gray-400'}`} />
                  <div>
                    <p className="font-semibold text-gray-900 text-sm mb-1">
                      {randomizeOptions ? 'Options Shuffled' : 'Options Not Shuffled'}
                    </p>
                    <p className="text-xs text-gray-600">
                      {randomizeOptions ? 'Answer choices are randomized' : 'Answer choices in fixed order'}
                    </p>
                  </div>
                </div>
              </div>
            </div>


          </div>
        </div>
      </main>

      {/* Start Quiz Confirmation Modal */}
      {showStartConfirmation && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ backgroundColor: 'rgba(15, 23, 42, 0.5)', backdropFilter: 'blur(8px)' }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            {/* Amber Warning Header */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 px-6 pt-6 pb-5 border-b border-amber-200">
              <div className="mx-auto mb-3 flex justify-center">
                <img
                  src={warningIcon}
                  alt="Warning"
                  className="w-16 h-16 sm:w-20 sm:h-20 object-contain drop-shadow-md"
                />
              </div>

              <h3 className="text-xl font-bold text-slate-900 mb-1 text-center">
                Start Quiz Attempt?
              </h3>
              <p className="text-xs text-amber-700 text-center font-medium">
                Please read carefully before proceeding
              </p>
            </div>

            {/* Content */}
            <div className="px-6 py-5">
              {/* Critical Warning Text */}
              <div className="mb-5">
                <p className="text-sm text-slate-700 leading-relaxed">
                  Please ensure you have a <span className="font-bold text-slate-900">stable internet connection</span> and enough uninterrupted time before proceeding.
                </p>

                {quiz?.settings?.timeLimit ? (
                  // WITH TIME LIMIT
                  <>
                    <p className="text-sm text-slate-700 leading-relaxed mt-3">
                      Once you start, <span className="font-bold text-slate-900">the quiz cannot be paused.</span> Closing your browser or leaving early will automatically force-submit your attempt and <span className="font-bold text-slate-900">may result in a score of 0.</span>
                    </p>
                    <p className="text-sm text-slate-700 leading-relaxed mt-3">
                      ⏱️ Time limit: <span className="font-bold text-slate-900">{quiz.settings.timeLimit} minutes</span> - quiz auto-submits when time runs out.
                    </p>
                  </>
                ) : (
                  // UNLIMITED TIME
                  <>
                    <p className="text-sm text-slate-700 leading-relaxed mt-3">
                      This quiz has <span className="font-bold text-emerald-600">unlimited time</span>, yet you <span className="font-bold text-slate-900">must submit it at the end of your session</span> to be properly scored.
                    </p>
                    <p className="text-sm text-slate-700 leading-relaxed mt-3">
                      Closing your browser or leaving early <span className="font-bold text-slate-900">without submitting will result in your attempt not being recorded.</span>
                    </p>
                  </>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3">
                <button
                  onClick={() => setShowStartConfirmation(false)}
                  className="flex-1 px-5 py-2.5 bg-slate-100 border border-slate-300 text-slate-600 rounded-lg font-semibold hover:bg-slate-200 transition-all text-sm"
                >
                  Cancel
                </button>

                <button
                  onClick={confirmStartQuiz}
                  className="flex-1 px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg font-semibold transition-all shadow-md hover:shadow-lg text-sm"
                >
                  Let's Begin
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default QuizOverview;
